import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { mediaSrc } from '../api/media';

interface ChatSummary {
  conversationId: number;
  type: 'PARENT_KID' | 'GROUP';
  title: string;
  photoUrl: string | null;
  lastMessageAt: string | null;
  lastPreview: string | null;
  unread: number;
  closed: boolean;
}
interface Message {
  id: number;
  senderType: 'PARENT' | 'KID';
  senderId: number;
  senderName: string;
  body: string;
  createdAt: string;
  mine: boolean;
}

const KidChat: React.FC = () => {
  const { userId } = useAuthStore();
  const [params] = useSearchParams();
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const lastIdRef = useRef(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  const loadChats = useCallback(() => {
    api.get(`/api/kids/${userId}/chats`).then((r) => setChats(r.data)).catch(() => {});
  }, [userId]);

  useEffect(() => { loadChats(); }, [loadChats]);

  // auto-open a conversation passed via ?cid=
  useEffect(() => {
    const cid = params.get('cid');
    if (cid) setActiveId(Number(cid));
  }, [params]);

  // full reload when switching conversation
  useEffect(() => {
    if (activeId == null) return;
    let cancelled = false;
    lastIdRef.current = 0;
    setMessages([]);
    api.get(`/api/kids/${userId}/chats/${activeId}/messages`).then((r) => {
      if (cancelled) return;
      setMessages(r.data);
      if (r.data.length) lastIdRef.current = r.data[r.data.length - 1].id;
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [activeId, userId]);

  // poll for new messages every 3s
  useEffect(() => {
    if (activeId == null) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/api/kids/${userId}/chats/${activeId}/messages`, { params: { afterId: lastIdRef.current } });
        if (r.data.length) {
          setMessages((prev) => [...prev, ...r.data]);
          lastIdRef.current = r.data[r.data.length - 1].id;
        }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [activeId, userId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || activeId == null) return;
    const body = text;
    setText('');
    try {
      const r = await api.post(`/api/kids/${userId}/chats/${activeId}/messages`, { body });
      setMessages((prev) => [...prev, r.data]);
      lastIdRef.current = r.data.id;
      loadChats();
    } catch { setText(body); }
  };

  const active = chats.find((c) => c.conversationId === activeId);

  return (
    <div className="flex h-[68vh] rounded-2xl overflow-hidden border border-gray-100 bg-white">
      {/* chat list */}
      <div className={`${activeId != null ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-72 border-r border-gray-200 bg-white`}>
        <h1 className="text-lg font-bold text-brand-purple px-4 py-4 border-b border-gray-100">💬 Chat</h1>
        <div className="flex-1 overflow-y-auto">
          {chats.length === 0 ? (
            <p className="text-sm text-gray-400 p-4">No chats yet. A parent can start one with you.</p>
          ) : chats.map((c) => (
            <button key={c.conversationId} onClick={() => setActiveId(c.conversationId)}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 ${activeId === c.conversationId ? 'bg-purple-50' : ''}`}>
              {c.type === 'PARENT_KID' && c.photoUrl ? (
                <img src={mediaSrc(c.photoUrl)} alt="" className="w-9 h-9 rounded-full object-cover" />
              ) : (
                <span className="w-9 h-9 rounded-full bg-purple-100 inline-flex items-center justify-center">{c.type === 'GROUP' ? '👪' : '👨‍👩‍👧'}</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-700 truncate">{c.title}</p>
                <p className="text-xs text-gray-400 truncate">{c.lastPreview || 'No messages yet'}</p>
              </div>
              {c.unread > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{c.unread}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* thread */}
      <div className={`${activeId == null ? 'hidden md:flex' : 'flex'} flex-col flex-1`}>
        {active == null ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a chat</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center gap-2">
              <button onClick={() => setActiveId(null)} className="md:hidden text-brand-purple">←</button>
              <span className="font-semibold text-gray-700">{active.title}</span>
              <span className="text-xs text-gray-400">{active.type === 'GROUP' ? '· group' : ''}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.mine ? 'bg-brand-purple text-white' : 'bg-white text-gray-700 shadow-sm'}`}>
                    {!m.mine && active.type === 'GROUP' && <p className="text-[11px] font-semibold text-purple-500 mb-0.5">{m.senderName}</p>}
                    {m.body}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="p-3 border-t border-gray-200 bg-white flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
                className="flex-1 border rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              <button type="submit" className="bg-brand-purple text-white text-sm px-4 py-2 rounded-full">Send</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default KidChat;
